import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import SeoHead from '../components/SeoHead'
import HeroVideo from '../components/HeroVideo'
import Lightbox from '../components/Lightbox'
import { assetPath } from '../lib/images'
import { HanddrawnArrow, HanddrawnDecorativeArrow } from '../components/HanddrawnArrow'
import { ArcOrbitButton } from '../components/ArcOrbitButton'

// Autentisk opslag af valgfagsikoner fra eeskole.dk til at ligge til venstre for teksten
const getValgfagIconData = (item) => {
  if (Array.isArray(item.icon_image) && item.icon_image[0]) {
    return { type: 'img', src: assetPath(item.icon_image[0]) }
  }
  if (typeof item.icon_image === 'string' && item.icon_image) {
    return { type: 'img', src: assetPath(item.icon_image) }
  }
  const t = (item.title || item.tag || '').toLowerCase()
  if (t.includes('krea') || t.includes('sysler')) return { type: 'img', src: '/_assets/krea_644beef9.png' }
  if (t.includes('fit') || t.includes('styrk') || t.includes('body')) return { type: 'img', src: '/_assets/body_8ec230de.png' }
  if (t.includes('spring') || t.includes('gym')) return { type: 'icon', iconClass: 'fa-solid fa-person-running' }
  if (t.includes('mindful')) return { type: 'icon', iconClass: 'fa-solid fa-spa' }
  if (t.includes('medie') || t.includes('foto') || t.includes('some')) return { type: 'icon', iconClass: 'fa-solid fa-camera' }
  if (t.includes('cyk')) return { type: 'icon', iconClass: 'fa-solid fa-bicycle' }
  if (t.includes('musik')) return { type: 'icon', iconClass: 'fa-solid fa-guitar' }
  if (t.includes('selvforsvar') || t.includes('kamp') || t.includes('judo')) return { type: 'icon', iconClass: 'fa-solid fa-shield-halved' }
  if (item.iconClass) return { type: 'icon', iconClass: item.iconClass }
  return { type: 'icon', iconClass: 'fa-solid fa-shapes' }
}

// Unikke organiske former for valgfag-kortene (billederne bevarer deres unikke organiske former, mens tekstkasserne har en uniform oval form og ligger lige horisontalt)
const VALGFAG_CARD_SHAPES = [
  {
    mediaRadius: '64% 36% 56% 44% / 44% 50% 50% 56%',
    mediaRadiusHover: '56% 44% 64% 36% / 48% 44% 56% 52%',
    badgeRadius: '9999px',
    badgeRadiusHover: '9999px',
    rotate: '0deg',
  },
  {
    mediaRadius: '42% 58% 48% 52% / 56% 42% 58% 44%',
    mediaRadiusHover: '48% 52% 54% 46% / 48% 48% 52% 52%',
    badgeRadius: '9999px',
    badgeRadiusHover: '9999px',
    rotate: '0deg',
  },
  {
    mediaRadius: '56% 44% 38% 62% / 40% 60% 42% 58%',
    mediaRadiusHover: '48% 52% 46% 54% / 46% 54% 48% 52%',
    badgeRadius: '9999px',
    badgeRadiusHover: '9999px',
    rotate: '0deg',
  },
  {
    mediaRadius: '66% 34% 50% 50% / 48% 44% 56% 52%',
    mediaRadiusHover: '58% 42% 56% 44% / 54% 48% 52% 46%',
    badgeRadius: '9999px',
    badgeRadiusHover: '9999px',
    rotate: '0deg',
  },
  {
    mediaRadius: '44% 56% 62% 38% / 48% 56% 44% 52%',
    mediaRadiusHover: '50% 50% 54% 46% / 44% 52% 48% 56%',
    badgeRadius: '9999px',
    badgeRadiusHover: '9999px',
    rotate: '0deg',
  },
  {
    mediaRadius: '52% 48% 64% 36% / 44% 58% 42% 56%',
    mediaRadiusHover: '46% 54% 56% 44% / 48% 52% 48% 52%',
    badgeRadius: '9999px',
    badgeRadiusHover: '9999px',
    rotate: '0deg',
  },
  {
    mediaRadius: '58% 42% 46% 54% / 54% 44% 56% 46%',
    mediaRadiusHover: '52% 48% 52% 48% / 48% 50% 50% 52%',
    badgeRadius: '9999px',
    badgeRadiusHover: '9999px',
    rotate: '0deg',
  },
  {
    mediaRadius: '48% 52% 54% 46% / 42% 58% 46% 54%',
    mediaRadiusHover: '54% 46% 48% 52% / 48% 52% 52% 48%',
    badgeRadius: '9999px',
    badgeRadiusHover: '9999px',
    rotate: '0deg',
  },
]

export default function HomePage() {
  const [valgfagList, setValgfagList] = useState([])
  const [galleryLightboxIndex, setGalleryLightboxIndex] = useState(null)
  const [mapInView, setMapInView] = useState(false)
  const mapSectionRef = useRef(null)

  // Autentiske fotografier fra Emmerske Efterskole til collager og galleri
  const schoolLifePhotos = [
    '/_assets/dsc00737_b28d969a.jpg',
    '/_assets/dsc00815_f86d9057.jpg',
    '/_assets/dsc00901_0b4af1e4.jpg',
    '/_assets/dsc00981_2e69773d.jpg',
    '/_assets/dsc00991-1_bd637d45.jpg',
    '/_assets/dsc00998-1_20e0f98a.jpg',
    '/_assets/dsc01007_be359ec3.jpg',
    '/_assets/dsc01025_c49f68ab.jpg',
    '/_assets/dsc01030_e6621df6.jpg',
    '/_assets/dsc01047_578d4989.jpg',
    '/_assets/dsc01094_7872ed6d.jpg',
    '/_assets/dsc01126-0_1c1545bb.jpg',
    '/_assets/dsc00973_1268c8a9.jpg',
    '/_assets/dsc01164_b0f51fa9.jpg',
    '/_assets/dsc01037_cec92b3d.jpg',
    '/_assets/dsc00755_e31a980f.jpg',
    '/_assets/dsc00965_27a565d8.jpg',
    '/_assets/dsc01421-0_e9196308.jpg',
    '/_assets/dsc00976_f5bc3924.jpg',
    '/_assets/dsc01109_3b5b4903.jpg',
    '/_assets/efterskole-outdoor-elever-haengekoeje_245ff15c.jpg',
    '/_assets/dsc01454_44d09c6e.jpg',
    '/_assets/dsc00940_ce2ac6ab.jpg',
    '/_assets/dsc00774_4dd65579.jpg',
  ]

  // Default / fallback valgfag cards if CMS is loading
  const fallbackValgfag = [
    {
      id: 'krea',
      title: 'KREA',
      tag: 'Kreativitet & hygge',
      badgeColor: '#007A53',
      iconClass: 'fa-solid fa-palette',
      short_desc:
        'Kreativitet, fordybelse og hyggeligt fællesskab med maleri, kultegning, mosaik og genbrugskunst.',
      image: ['/_assets/krea-valgfag_13432e92.jpg'],
      link: '/undervisning/valgfag/krea',
    },
    {
      id: 'fitness',
      title: 'Fitness',
      tag: 'Styrke & kondition',
      badgeColor: '#2F6F73',
      iconClass: 'fa-solid fa-dumbbell',
      short_desc:
        'Styrketræning, kondition og sjove træningsøvelser i dit eget tempo for alle niveauer.',
      image: ['https://assets.auxodev.dk/assets/e40f0695-6390-4586-b67e-d6f4d574a533/fitness.jpg'],
      link: '/undervisning/valgfag/fitness',
    },
    {
      id: 'spring',
      title: 'Spring',
      tag: 'Gymnastik & trampolin',
      badgeColor: '#36454F',
      iconClass: 'fa-solid fa-person-running',
      short_desc:
        'Udfordr dig selv på trampolin og bane – fra grundlæggende spring til flikflak, saltoer og opvisning.',
      image: ['/_assets/spring-valgfag.jpg'],
      link: '/undervisning/valgfag/spring',
    },
    {
      id: 'mindfulness',
      title: 'Mindfulness',
      tag: 'Ro & fordybelse',
      badgeColor: '#2F6F73',
      iconClass: 'fa-solid fa-spa',
      short_desc:
        'Skab et lille frirum fra hverdagens tempo med ro, nærvær, vejrtrækningsøvelser og blød krea.',
      image: ['https://assets.auxodev.dk/assets/e40f0695-6390-4586-b67e-d6f4d574a533/hygge-i-krea.jpg'],
      link: '/undervisning/valgfag/mindfulness',
    },
    {
      id: 'mediefag',
      title: 'Mediefag',
      tag: 'Fotografering & SoMe',
      badgeColor: '#007A53',
      iconClass: 'fa-solid fa-camera',
      short_desc:
        'Fotografiets verden med lys, vinkler og komposition samt tryg og ansvarlig adfærd på sociale medier.',
      image: ['https://assets.auxodev.dk/assets/e40f0695-6390-4586-b67e-d6f4d574a533/mediefag.jpg'],
      link: '/undervisning/valgfag/mediefag',
    },
    {
      id: 'cykling',
      title: 'Cykling',
      tag: 'Motion & natur',
      badgeColor: '#2F6F73',
      iconClass: 'fa-solid fa-bicycle',
      short_desc:
        'Cykelture i Tønders smukke natur, mountainbike i skoven, konditionstræning og sundhed.',
      image: ['https://assets.auxodev.dk/assets/e40f0695-6390-4586-b67e-d6f4d574a533/cykel.jpg'],
      link: '/undervisning/valgfag/cykling',
    },
    {
      id: 'musik',
      title: 'Musik',
      tag: 'Musik & samspil',
      badgeColor: '#36454F',
      iconClass: 'fa-solid fa-guitar',
      short_desc:
        'Spilleglæde, samspil og musikfællesskab med instrumenter og musikteori i øjenhøjde for alle niveauer.',
      image: ['https://assets.auxodev.dk/assets/e40f0695-6390-4586-b67e-d6f4d574a533/musik.jpg'],
      link: '/undervisning/valgfag/musik',
    },
    {
      id: 'selvforsvar',
      title: 'Selvforsvar',
      tag: 'Kampsport & tryghed',
      badgeColor: '#007A53',
      iconClass: 'fa-solid fa-shield-halved',
      short_desc:
        'Kampsport, frigørelse fra greb, kropskontrol og selvtillid med Danmarksmester-underviser i trygge rammer.',
      image: ['https://assets.auxodev.dk/assets/e40f0695-6390-4586-b67e-d6f4d574a533/selvforsvar.jpg'],
      link: '/undervisning/valgfag/selvforsvar',
    },
  ]

  useEffect(() => {
    fetch('/_api/public/content/valgfag')
      .then((res) => {
        if (!res.ok) throw new Error('Kunne ikke hente valgfag')
        return res.json()
      })
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          const sorted = [...data].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
          setValgfagList(sorted)
        }
      })
      .catch((err) => {
        console.warn('Valgfag fetch note:', err)
      })
  }, [])

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setMapInView(entry.isIntersecting)
      },
      { threshold: 0.25 }
    )

    if (mapSectionRef.current) {
      observer.observe(mapSectionRef.current)
    }

    return () => observer.disconnect()
  }, [])

  const displayValgfag = valgfagList.length > 0 ? valgfagList : fallbackValgfag

  // Authentisk information om hverdagen og fællesskabet direkte fra skolen
  const communityCards = [
    {
      title: 'Fællesskab & Sammenhold',
      desc: 'Et unikt sammenhold med de øvrige elever, hvor der skabes minder for livet. Du får nye venner, som ofte har de samme udfordringer med ordblindhed som dig.',
      icon: 'fa-solid fa-users',
      shape: 'blob-shape-1',
      bg: 'bg-[#007A53]',
    },
    {
      title: 'Fritiden på skolen',
      desc: 'I fritiden kan du arbejde i træ- og metalværkstedet, dyrke sport i hallen, hygge i opholdsstuerne eller spille med vennerne på værelserne.',
      icon: 'fa-solid fa-gamepad',
      shape: 'blob-shape-2',
      bg: 'bg-[#2F6F73]',
    },
    {
      title: 'Trygge kontaktgrupper',
      desc: 'Hver elev har en fast kontaktlærer med ca. 10 elever i gruppen. Kontaktlæreren er det trygge bindeled mellem dig, skolen og dine forældre.',
      icon: 'fa-solid fa-hand-holding-heart',
      shape: 'blob-shape-3',
      bg: 'bg-[#36454F]',
    },
    {
      title: 'Weekender & Lejrskole',
      desc: 'Du kan blive på skolen i weekenderne til film, bowling, svømmehal eller bare afslapning – og en gang om året tager alle elever på fælles lejrskole i udlandet – helt uden ekstra omkostninger.',
      icon: 'fa-solid fa-plane-departure',
      shape: 'blob-shape-1',
      bg: 'bg-[#2F6F73]',
    },
  ]

  return (
    <div className="w-full bg-white overflow-hidden">
      <SeoHead
        isHome={true}
        title="Emmerske Efterskole"
        description="Emmerske Efterskole er en specialiseret efterskole for ordblinde i Sønderjylland med plads til 95 elever i 8., 9. og 10. klasse. Her er det rart at være og lære."
      />

      {/* 1. HERO MED FILM OG CENTRERET KOMPOSITION */}
      <HeroVideo />

      {/* 2. FØRSTE INDHOLDSSEKTION – BILLEDE OG ORGANISK FLOATING BLOB (AFSNIT 5) */}
      <section className="py-20 sm:py-28 lg:py-36 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative" aria-label="Introduktion til Emmerske Efterskole">
        {/* Dekorativ blød baggrunds-blob */}
        <div className="background-blob -top-16 -left-20 opacity-40" aria-hidden="true" />

        <div className="grid grid-cols-1 lg:grid-cols-12 items-center gap-12 lg:gap-16 relative z-10">
          
          {/* Venstre side: Stort redaktionelt billede med asymmetrisk organisk maske og overlappende floating blob */}
          <div className="lg:col-span-6 relative">
            <div className="relative w-full max-w-[540px] mx-auto lg:max-w-none">
              {/* Hovedbillede med organisk form og skygge */}
              <div className="relative h-[380px] sm:h-[480px] lg:h-[520px] rounded-organic overflow-hidden shadow-[0_20px_50px_rgba(47,111,115,0.22)] border-4 sm:border-8 border-white bg-[#DCEEF2] group">
                <img
                  src="/_assets/elever-undervisning-emmerske-efterskole-toender_02d69491.jpg"
                  alt="Læring og trygt fællesskab på Emmerske Efterskole"
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                />
              </div>

              {/* Sekundært overlappende lille rundt billede */}
              <div className="absolute -bottom-6 -left-4 sm:bottom-0 sm:-left-6 w-36 h-36 sm:w-44 sm:h-44 rounded-full overflow-hidden border-4 border-white shadow-xl z-20 hidden sm:block">
                <img
                  src="/_assets/elever-efterskole-soenderjylland_ec11372b.jpg"
                  alt="Glade elever på Emmerske"
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Organisk Floating Blob badge delvist oven på billedet */}
              <div
                className="floating-blob absolute -top-6 -right-4 sm:-top-8 sm:-right-8 z-30"
                aria-label="Grundlagt i 1977"
              >
                <div className="text-center p-4">
                  <span className="font-['Shadows_Into_Light'] text-2xl sm:text-3xl font-bold leading-none block text-[#DCEEF2]">
                    Siden
                  </span>
                  <span className="font-['Plus_Jakarta_Sans'] text-2xl sm:text-3xl font-black block tracking-tight">
                    1977
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Højre side: Tunge overskrifter, Caveat håndskrift og redaktionel tekst */}
          <div className="lg:col-span-6 space-y-6">
            <div>
              <span className="handwritten text-[#007A53] mb-6 block">
                Porten til Europa & Tøndermarsken
              </span>
              <h2 className="font-['Plus_Jakarta_Sans'] text-3xl sm:text-4xl lg:text-5xl font-black text-[#36454F] tracking-tight leading-[1.1]">
                På Emmerske Efterskole møder vi dig som den, du er
              </h2>
            </div>

            <div className="space-y-4 text-base sm:text-lg text-[#36454F]/90 leading-relaxed font-normal">
              <p>
                Vi har siden 1977 hjulpet mange ordblinde med deres læse- og skrivevanskeligheder.
                Vores unikke placering ved porten til Europa og Tøndermarskens helt særlige natur åbner
                mange muligheder for gode oplevelser.
              </p>
              <p>
                På vores ordblindeefterskole vil vi gøre alt for, at dit efterskoleår bliver fyldt med
                gode oplevelser, venner for livet og et stærkt fællesskab.
              </p>
              <p className="font-bold text-[#007A53]">
                Vi glæder os til at møde dig og din familie.
              </p>
            </div>

            <div className="pt-4 flex flex-wrap items-center gap-4">
              <ArcOrbitButton to="/om-os">
                Om Emmerske Efterskole
              </ArcOrbitButton>
              <ArcOrbitButton
                to="/rundvisning"
                variant="outline"
                arrow={false}
              >
                Book rundvisning
              </ArcOrbitButton>
            </div>
          </div>
        </div>
      </section>

      {/* 3. STORE ORGANISKE INDHOLDSKORT I INFINITE MARQUEE (AFSNIT 7 & 8) */}
      <section
        className="py-20 sm:py-28 bg-[#DCEEF2] relative overflow-hidden"
        aria-label="Valgfag på Emmerske Efterskole"
      >
        {/* Dekorativ baggrunds-blob */}
        <div className="background-blob -bottom-20 -right-20 bg-[#2F6F73]/15" aria-hidden="true" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-10 sm:mb-14 text-center">
          <span className="handwritten text-[#007A53] mb-6 block">
            For den kreative såvel som den aktive elev
          </span>
          <h2 className="font-['Plus_Jakarta_Sans'] text-3xl sm:text-4xl lg:text-5xl font-black text-[#36454F] tracking-tight">
            Valgfag på Emmerske Efterskole
          </h2>
          <p className="mt-3 text-base sm:text-lg text-[#36454F]/85 max-w-2xl mx-auto">
            Hvert valgfag byder på praktiske færdigheder, hyggelig atmosfære og stærkt sammenhold. Hold
            musen over et fag for at udforske det.
          </p>
        </div>

        {/* Kontinuerlig Infinite Marquee Track med 2 identiske grupper for sømløs rullende bevægelse */}
        <div className="marquee pb-6 pt-2">
          <div className="marquee__track">
            {/* Første gruppe */}
            {displayValgfag.map((item, idx) => {
              const bgColors = ['#007A53', '#2F6F73', '#36454F']
              const badgeBg = item.badgeColor || bgColors[idx % bgColors.length]
              const rawImg = Array.isArray(item.image) && item.image[0] ? item.image[0] : item.image
              const imgSrc = rawImg ? assetPath(rawImg) : '/_assets/efterskole-med-haandvaerksfag-toender_96bb1e42.jpg'
              const linkTarget = item.link || `/undervisning/valgfag`
              const iconData = getValgfagIconData(item)
              const shape = VALGFAG_CARD_SHAPES[idx % VALGFAG_CARD_SHAPES.length]

              return (
                <Link
                  key={`item-1-${item.id || idx}`}
                  to={linkTarget}
                  className="organic-card group"
                  aria-label={`Læs mere om valgfaget ${item.title}`}
                  style={{
                    '--card-media-radius': shape.mediaRadius,
                    '--card-media-radius-hover': shape.mediaRadiusHover,
                    '--card-badge-radius': shape.badgeRadius,
                    '--card-badge-radius-hover': shape.badgeRadiusHover,
                    '--card-badge-rotate': shape.rotate,
                  }}
                >
                  {/* Stort organisk beskåret billede */}
                  <div className="organic-card__media">
                    <img
                      className={`transition-transform duration-500 ease-out group-hover:scale-[1.08] group-focus-visible:scale-[1.08] motion-reduce:transition-none ${
                        item.id === 'cykling' || item.slug?._ === 'cykling' || (item.title && item.title.toLowerCase() === 'cykling')
                          ? 'object-[48%_32%]'
                          : ''
                      }`}
                      src={imgSrc}
                      alt={item.title}
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
                  </div>

                  {/* Overlappende organisk tekstbadge med autentisk ikon til venstre for teksten */}
                  <div
                    className="organic-card__badge !px-6 sm:!px-7 !py-2.5 sm:!py-3"
                    style={{ backgroundColor: badgeBg }}
                  >
                    <div className="flex items-center gap-4 w-full">
                      {/* Ikon beholder til venstre for teksten */}
                      <div className="w-12 h-12 sm:w-13 sm:h-13 flex items-center justify-center shrink-0">
                        {iconData.type === 'img' ? (
                          <img
                            src={iconData.src}
                            alt=""
                            aria-hidden="true"
                            className="w-full h-full object-contain filter brightness-0 invert drop-shadow-sm"
                          />
                        ) : (
                          <i className={`${iconData.iconClass} text-2xl sm:text-3xl text-white`} aria-hidden="true" />
                        )}
                      </div>

                      {/* Tekstblok til højre for ikonet med god indvendig margin/padding */}
                      <div className="flex-1 min-w-0 pr-2">
                        <h3 className="font-['Plus_Jakarta_Sans'] text-base sm:text-lg font-black text-white uppercase tracking-wider leading-snug mb-1 truncate">
                          {item.title}
                        </h3>
                        <p className="text-xs sm:text-[13px] text-white/95 line-clamp-2 leading-relaxed font-medium break-words">
                          {item.short_desc}
                        </p>
                      </div>
                    </div>
                  </div>
                </Link>
              )
            })}

            {/* Anden identiske gruppe for 100% sømløst loop uden spring */}
            {displayValgfag.map((item, idx) => {
              const bgColors = ['#007A53', '#2F6F73', '#36454F']
              const badgeBg = item.badgeColor || bgColors[idx % bgColors.length]
              const rawImg = Array.isArray(item.image) && item.image[0] ? item.image[0] : item.image
              const imgSrc = rawImg ? assetPath(rawImg) : '/_assets/efterskole-med-haandvaerksfag-toender_96bb1e42.jpg'
              const linkTarget = item.link || `/undervisning/valgfag`
              const iconData = getValgfagIconData(item)
              const shape = VALGFAG_CARD_SHAPES[idx % VALGFAG_CARD_SHAPES.length]

              return (
                <Link
                  key={`item-2-${item.id || idx}`}
                  to={linkTarget}
                  className="organic-card group"
                  aria-hidden="true"
                  tabIndex={-1}
                  style={{
                    '--card-media-radius': shape.mediaRadius,
                    '--card-media-radius-hover': shape.mediaRadiusHover,
                    '--card-badge-radius': shape.badgeRadius,
                    '--card-badge-radius-hover': shape.badgeRadiusHover,
                    '--card-badge-rotate': shape.rotate,
                  }}
                >
                  <div className="organic-card__media">
                    <img
                      className="transition-transform duration-500 ease-out group-hover:scale-[1.08] motion-reduce:transition-none"
                      src={imgSrc}
                      alt={item.title}
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
                  </div>

                  <div
                    className="organic-card__badge !px-6 sm:!px-7 !py-2.5 sm:!py-3"
                    style={{ backgroundColor: badgeBg }}
                  >
                    <div className="flex items-center gap-4 w-full">
                      <div className="w-12 h-12 sm:w-13 sm:h-13 flex items-center justify-center shrink-0">
                        {iconData.type === 'img' ? (
                          <img
                            src={iconData.src}
                            alt=""
                            aria-hidden="true"
                            className="w-full h-full object-contain filter brightness-0 invert drop-shadow-sm"
                          />
                        ) : (
                          <i className={`${iconData.iconClass} text-2xl sm:text-3xl text-white`} aria-hidden="true" />
                        )}
                      </div>

                      <div className="flex-1 min-w-0 pr-2">
                        <h3 className="font-['Plus_Jakarta_Sans'] text-base sm:text-lg font-black text-white uppercase tracking-wider leading-snug mb-1 truncate">
                          {item.title}
                        </h3>
                        <p className="text-xs sm:text-[13px] text-white/95 line-clamp-2 leading-relaxed font-medium break-words">
                          {item.short_desc}
                        </p>
                      </div>
                    </div>
                  </div>
                </Link>
              )
            })}
          </div>
        </div>

        {/* CTA link under karrusellen */}
        <div className="text-center mt-6 sm:mt-10 relative z-10">
          <ArcOrbitButton
            to="/undervisning/valgfag"
            variant="primary"
            className="inline-flex"
          >
            Se alle {displayValgfag.length} valgfag på skolen
          </ArcOrbitButton>
        </div>
      </section>

      {/* 4. MØRKERE EDITORIAL SEKTION I PETROL (#2F6F73) (AFSNIT 10, SEKTIONSTYPE C) */}
      <section
        className="py-20 sm:py-28 lg:py-32 bg-[#2F6F73] text-white relative overflow-hidden"
        aria-label="49 års erfaring med ordblinde elever"
      >
        {/* Dekorativ organisk form i baggrunden */}
        <div className="absolute -top-32 -left-32 w-96 h-96 rounded-organic bg-white/5 pointer-events-none" />
        <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-organic bg-[#007A53]/25 pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 items-center gap-12 lg:gap-16">
            
            {/* Tekst og introduktion */}
            <div className="lg:col-span-7 space-y-6 text-left">
              <div>
                <span className="handwritten text-[#DCEEF2] mb-6 block">
                  Den kreative såvel som den aktive elev
                </span>
                <h2 className="font-['Plus_Jakarta_Sans'] text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight leading-[1.1]">
                  49 års erfaring med at gøre ordblinde elever uddannelsesparate
                </h2>
              </div>

              <div className="space-y-4 text-white/90 text-base sm:text-lg leading-relaxed font-normal">
                <p>
                  Som en af Danmarks ordblindeefterskoler tilbyder vi målrettet støtte med små klasser og
                  moderne hjælpemidler. Vi er en specialiseret efterskole i nære Tønder i Sønderjylland
                  med plads til 95 elever fordelt på 8., 9. og 10. årgang.
                </p>
                <p>
                  Vi tilbyder et udvalg af spændende valgfag, som tiltaler den kreative såvel som den
                  aktive elev, og vi er stolte af vores gode værkstedsfaciliteter, som bruges flittigt i
                  både undervisningen og fritiden.
                </p>
              </div>

              <div className="pt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <ArcOrbitButton
                  to="/rundvisning"
                  variant="white"
                  icon={<i className="fa-solid fa-calendar-check text-[#007A53] text-base" aria-hidden="true" />}
                  className="w-full sm:w-auto text-center"
                >
                  Book en personlig rundvisning
                </ArcOrbitButton>

                <ArcOrbitButton
                  to="/kontakt"
                  variant="outline-white"
                  arrow={false}
                  className="w-full sm:w-auto text-center"
                >
                  Kontakt os i dag
                </ArcOrbitButton>
              </div>
            </div>

            {/* Højre side: Kreativ asymmetrisk fotokomposition med organiske kurver */}
            <div className="lg:col-span-5 relative">
              <div className="relative w-full max-w-[460px] mx-auto h-[380px] sm:h-[460px]">
                {/* Hovedbillede med organisk kurve */}
                <div
                  className="absolute inset-0 overflow-hidden border-4 border-white/90 shadow-2xl z-10 group transition-all duration-700 ease-out hover:shadow-3xl"
                  style={{
                    borderRadius: '58% 42% 66% 34% / 44% 59% 41% 56%',
                    transform: 'rotate(-0.8deg)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderRadius = '46% 54% 39% 61% / 59% 43% 57% 41%'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderRadius = '58% 42% 66% 34% / 44% 59% 41% 56%'
                  }}
                >
                  <img
                    src="/_assets/dsc00973_1268c8a9.jpg"
                    alt="Glade elever og fællesskab på Emmerske Efterskole"
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#2F6F73]/40 via-transparent to-transparent" />
                </div>

                {/* Overlappende badge med Caveat tekst - uniform oval ramme */}
                <div className="absolute -bottom-4 -right-4 sm:-bottom-6 sm:-right-6 !rounded-full !px-8 sm:!px-10 !py-3.5 sm:!py-4 !rotate-0 bg-[#007A53] text-white shadow-xl z-20 flex flex-col items-center justify-center text-center transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl">
                  <span className="font-['Shadows_Into_Light'] text-2xl text-[#DCEEF2] block leading-none">
                    Trygt og tæt fællesskab
                  </span>
                  <span className="font-['Plus_Jakarta_Sans'] text-xl font-black block text-white mt-1">
                    Små klasser
                  </span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 5. BILLEDCOLLAGE MED HORISONTAL BEVÆGELSE (AFSNIT 9) */}
      <section className="py-20 sm:py-28 bg-white relative overflow-hidden" aria-label="Dagligdagen på Emmerske Efterskole">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-10 text-center">
          <span className="handwritten text-[#007A53] mb-6 block">
            Minder for livet
          </span>
          <h2 className="font-['Plus_Jakarta_Sans'] text-3xl sm:text-4xl lg:text-5xl font-black text-[#36454F] tracking-tight">
            Dagligdagen på Emmerske Efterskole
          </h2>
          <p className="mt-2 text-base sm:text-lg text-[#36454F]/80 max-w-xl mx-auto">
            Glimt af hverdagen, lejrskole, værksteder og nye venskaber. Klik på et billede for at se
            det i fuld skærm.
          </p>
        </div>

        {/* Automatisk vandret bevægende fotocollage med varierende højder og proportioner */}
        <div className="marquee pb-4 pt-2">
          <div className="marquee__track items-center">
            {/* Første omgang af 12 billeder med varieret højde og organisk radius */}
            {schoolLifePhotos.slice(0, 12).map((photoSrc, idx) => {
              const heights = ['h-64 sm:h-80', 'h-72 sm:h-96', 'h-60 sm:h-72', 'h-80 sm:h-[440px]', 'h-64 sm:h-84']
              const radii = [
                'rounded-organic',
                'rounded-organic',
                'rounded-organic',
                'rounded-3xl',
                'rounded-organic',
              ]
              const rotations = ['-rotate-1', 'rotate-2', '-rotate-2', 'rotate-1', 'rotate-0']
              const h = heights[idx % heights.length]
              const r = radii[idx % radii.length]
              const rot = rotations[idx % rotations.length]

              return (
                <button
                  key={`photo-1-${idx}`}
                  type="button"
                  onClick={() => setGalleryLightboxIndex(idx)}
                  className={`flex-none w-64 sm:w-80 lg:w-96 ${h} ${r} ${rot} overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-103 cursor-pointer border-4 border-white group relative`}
                  aria-label={`Åbn foto ${idx + 1} i fuld skærm`}
                >
                  <img
                    src={photoSrc}
                    alt={`Dagligdagen på Emmerske Efterskole foto ${idx + 1}`}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-108 transition-transform duration-700 ease-out"
                  />
                  <div className="absolute inset-0 bg-[#007A53]/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <span className="w-10 h-10 rounded-full bg-white text-[#007A53] flex items-center justify-center shadow-md">
                      <i className="fa-solid fa-expand text-sm" />
                    </span>
                  </div>
                </button>
              )
            })}

            {/* Gentaget omgang for sømløs loop */}
            {schoolLifePhotos.slice(0, 12).map((photoSrc, idx) => {
              const heights = ['h-64 sm:h-80', 'h-72 sm:h-96', 'h-60 sm:h-72', 'h-80 sm:h-[440px]', 'h-64 sm:h-84']
              const radii = [
                'rounded-organic',
                'rounded-organic',
                'rounded-organic',
                'rounded-3xl',
                'rounded-organic',
              ]
              const rotations = ['-rotate-1', 'rotate-2', '-rotate-2', 'rotate-1', 'rotate-0']
              const h = heights[idx % heights.length]
              const r = radii[idx % radii.length]
              const rot = rotations[idx % rotations.length]

              return (
                <button
                  key={`photo-2-${idx}`}
                  type="button"
                  onClick={() => setGalleryLightboxIndex(idx)}
                  className={`flex-none w-64 sm:w-80 lg:w-96 ${h} ${r} ${rot} overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-103 cursor-pointer border-4 border-white group relative`}
                  aria-hidden="true"
                  tabIndex={-1}
                >
                  <img
                    src={photoSrc}
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-108 transition-transform duration-700 ease-out"
                  />
                  <div className="absolute inset-0 bg-[#007A53]/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <span className="w-10 h-10 rounded-full bg-white text-[#007A53] flex items-center justify-center shadow-md">
                      <i className="fa-solid fa-expand text-sm" />
                    </span>
                  </div>
                </button>
              )
            })}
          </div>
        </div>

        {/* Link til fuldt galleri */}
        <div className="text-center mt-8">
          <ArcOrbitButton
            to="/billeder"
            variant="primary"
            className="inline-flex"
          >
            Se hele billedgalleriet ({schoolLifePhotos.length} fotos)
          </ArcOrbitButton>
        </div>
      </section>

      {/* 6. INDHOLDSORIENTERET AFSLUTNING: FÆLLESSKAB & KORT (AFSNIT 10, SEKTIONSTYPE E) */}
      <section className="py-20 sm:py-28 bg-[#DCEEF2] relative overflow-hidden" aria-label="Fællesskabet og hverdagen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="handwritten text-[#007A53] mb-6 block">
              Tryghed, trivsel og nye venner
            </span>
            <h2 className="font-['Plus_Jakarta_Sans'] text-3xl sm:text-4xl lg:text-5xl font-black text-[#36454F] tracking-tight">
              Hvordan er det at gå på Emmerske?
            </h2>
            <p className="mt-3 text-base sm:text-lg text-[#36454F]/85">
              Et efterskoleår handler ikke kun om faglig udvikling. Det handler om at finde venner for
              livet og opleve et trygt fællesskab.
            </p>
          </div>

          {/* 4 Organiske indholdskort om hverdagen */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-16">
            {communityCards.map((card, i) => (
              <div
                key={card.title}
                className="bg-white rounded-3xl p-6 sm:p-7 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-102 flex flex-col justify-between border border-white/60 relative overflow-hidden group"
              >
                <div className="space-y-4">
                  <div
                    className={`w-14 h-14 ${card.shape} ${card.bg} text-white flex items-center justify-center text-xl shadow-md group-hover:scale-110 group-hover:rotate-6 transition-all duration-300`}
                  >
                    <i className={card.icon} aria-hidden="true" />
                  </div>
                  <h3 className="font-['Plus_Jakarta_Sans'] text-xl font-black text-[#36454F] tracking-tight">
                    {card.title}
                  </h3>
                  <p className="text-sm sm:text-base text-[#36454F]/85 leading-relaxed">
                    {card.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* FIND EMMERSKE EFTERSKOLE & GOOGLE MAPS KORTPANEL */}
          <div
            ref={mapSectionRef}
            className="rounded-[3rem] bg-white shadow-xl overflow-hidden grid grid-cols-1 lg:grid-cols-12 items-stretch border border-white/60"
          >
            {/* Google Maps med kortvisning */}
            <div className="lg:col-span-6 relative min-h-[360px] sm:min-h-[420px] bg-stone-100 overflow-hidden order-2 lg:order-1">
              <iframe
                title="Kort over Emmerske Efterskole"
                src="https://maps.google.com/maps?q=54.938889,8.905556&t=m&z=13&output=embed&iwloc="
                className="w-full h-full min-h-[360px] sm:min-h-[420px] lg:absolute lg:inset-0 border-0"
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />

              {/* Kort-ikon markering der toner blødt frem */}
              <div
                className={`absolute inset-0 pointer-events-none z-20 flex items-center justify-center p-6 select-none transition-all duration-700 ease-out ${
                  mapInView ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
                }`}
                aria-hidden="true"
              >
                <img
                  src="/_assets/kort-emmerske.png"
                  alt="Danmarkskort med markering af Emmerske Efterskole"
                  className="w-full h-full max-h-[340px] object-contain drop-shadow-2xl"
                  style={{ filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.45))' }}
                />
              </div>
            </div>

            {/* Højre side: Bliv klar til uddannelse og kontakt */}
            <div className="lg:col-span-6 p-8 sm:p-12 lg:p-16 flex flex-col justify-center space-y-6 bg-white order-1 lg:order-2">
              <div className="space-y-4">
                <span className="handwritten text-[#007A53] text-2xl block mb-6">
                  Klar til fremtiden
                </span>
                <h2 className="font-['Plus_Jakarta_Sans'] text-3xl sm:text-4xl font-black text-[#36454F] tracking-tight leading-tight">
                  Bliv klar til uddannelse på Emmerske Efterskole
                </h2>
                <p className="text-[#36454F]/85 text-base sm:text-lg leading-relaxed">
                  Vores primære fokus er, at du som elev på Emmerske Efterskole bliver uddannelsesparat
                  både fagligt, personligt og socialt. Book en rundvisning i dag og hør meget mere om
                  skolens tilbud og ordblindestøtte.
                </p>

                <div className="pt-2 flex flex-wrap gap-4">
                  <ArcOrbitButton
                    to="/rundvisning"
                    icon={<i className="fa-solid fa-calendar-check" aria-hidden="true" />}
                  >
                    Book rundvisning
                  </ArcOrbitButton>

                  <ArcOrbitButton
                    to="/kontakt"
                    variant="outline"
                    arrow={false}
                  >
                    Kontakt os
                  </ArcOrbitButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox til visning af billeder i fuld skærm */}
      {galleryLightboxIndex !== null && (
        <Lightbox
          images={schoolLifePhotos}
          initialIndex={galleryLightboxIndex}
          onClose={() => setGalleryLightboxIndex(null)}
        />
      )}
    </div>
  )
}
